<template>
    <div id="wrapper" class="position-relative w-100 overflow-hidden">
        <main id="main" class="py-60">
            <div class="container">
                <div class="box-shadow position-relative pt-50 pb-40">
                    <h2 class="text-center mb-35">
                        Step 2<span class="text-light-blue d-block">Please provide the flight information</span>
                    </h2>

                    <ValidationObserver v-slot="{ handleSubmit }">
                        <form action="#" v-on:submit.prevent="handleSubmit(saveTrip)"
                              class="main-form needs-validation text-pearl">
                            <div class="form-row mb-28">
                                <div class="col-md-6 mb-8">
                                    <ValidationProvider mode="eager" name="From" rules="required" v-slot="{ errors }">
                                        <Autocomplete
                                            :items="airportsFromQuery"
                                            v-model="airportFrom"
                                            :get-label="getLabel"
                                            :component-item='template'
                                            :input-attrs="fromInput"
                                            @input="refreshDistance"
                                            @change="clearClassFrom">
                                        </Autocomplete>
                                        <div class="valid-feedback">
                                            Looks good!
                                        </div>
                                        <div class="invalid-feedback">{{ errors[0] }}</div>
                                    </ValidationProvider>
                                    <div class="form-group form-check mt-8">
                                        <input v-model="nearbyFrom" type="checkbox"
                                               class="position-relative form-check-input" id="exampleCheck1">
                                        <label class="position-relative form-check-label" for="exampleCheck1">Nearby
                                            Airports</label>
                                    </div>
                                </div>

                                <div class="col-md-6 mb-18">
                                    <!-- <input type="text" class="form-control" id="validationCustom02" placeholder="To:" required> -->
                                    <ValidationProvider mode="eager" name="To" rules="required" v-slot="{ errors }">
                                        <Autocomplete
                                            :items="airportsToQuery"
                                            v-model="airportTo"
                                            :get-label="getLabel"
                                            :component-item='template'
                                            :input-attrs="toInput"
                                            @update-items="updateAirportsTo"
                                            @input="refreshDistance"
                                            @change="clearClassTo">
                                        </Autocomplete>
                                        <div class="valid-feedback">
                                            Looks good!
                                        </div>
                                        <div class="invalid-feedback">{{ errors[0] }}</div>
                                    </ValidationProvider>
                                    <div class="form-group form-check mt-8">
                                        <input v-model="nearbyTo" type="checkbox"
                                               class="position-relative form-check-input" id="exampleCheck2">
                                        <label class="position-relative form-check-label" for="exampleCheck2">Nearby
                                            Airports</label>
                                    </div>
                                </div>
                                <div class="col-md-12 mb-18">
                                    <span v-if="travelTime"
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9">{{ travelTime }}</span>
                                    <span v-else
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9"> --- Hrs.</span>
                                    <span v-if="distance"
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9"> {{ roundNumber(distance) }} Miles</span>
                                    <span v-else
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9"> --- Miles</span>
                                    <span v-if="estimatedPrice"
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9"> ${{ roundNumber(estimatedPrice) }}</span>
                                    <span v-else
                                          class="bg-light-blue text-white d-inline-block align-top border rounded px-20 py-9"> --- Estimated Price</span>
                                </div>
                                <div class="col-md-6 mb-30">
                                    <ValidationProvider mode="aggressive" name="Date Range" rules="required"
                                                        v-slot="{ errors }">
                                        <date-picker
                                            :key="datePickerKey"
                                            :editable="false"
                                            :input-class="'form-control date-picker-trips text-left'"
                                            format="MM-DD-YYYY"
                                            class="w-100 input-group"
                                            v-model="dateRange"
                                            valueType="format"
                                            :placeholder="'Date Range'"
                                            :default-value="new Date().setHours(0, 0, 0, 0)"
                                            :disabled-date="beforeToday"
                                            range>
                                        </date-picker>
                                        <div class="invalid-feedback">{{ errors[0] }}</div>
                                    </ValidationProvider>
                                    <!-- <div class="invalid-feedback">Please provide a valid Date.</div> -->
                                </div>
                                <div class="col-md-6 mb-18">
                                    <select v-model="timeOfDay" class="custom-select h-75">
                                        <option>Morning</option>
                                        <option>Afternoon</option>
                                        <option>Evening</option>
                                    </select>
                                </div>

                                <div v-if="displayTextArea" class="col-md-12 mb-30">
                                    <textarea placeholder="Note" class="form-control" v-model="description"
                                              rows="3"></textarea>
                                </div>

                                <div class="col-md-6">
                                    <div
                                        class="seat-input form-group d-flex align-items-center justify-content-md-end mb-30 mb-md-20">
                                        <ValidationProvider name="Seats Desired" :rules="seatsDesiredRules"
                                                            v-slot="{ errors }">
                                            <label for="passengersInput">Seats Desired:</label>
                                            <div class="input-wrapper mr-35 ml-15">
                                                <input id="passengersInput" autocomplete="off" v-model="passengers"
                                                       type="text" class="form-control num">
                                            </div>
                                            <div class="invalid-feedback">{{ errors[0] }}</div>
                                        </ValidationProvider>
                                    </div>
                                    <div
                                        class="seat-input form-group d-flex align-items-center justify-content-md-end position-relative mb-30 mb-md-0">
                                        <ValidationProvider mode="aggressive" name="Seat Capacity"
                                                            rules="required|integer|greaterthan:@Seats Desired"
                                                            v-slot="{ errors }">
                                            <label for="capacityInput">Seat Capacity:</label>
                                            <div class="input-wrapper mr-35 ml-15">
                                                <input v-model="capacity" autocomplete="off" type="text"
                                                       class="form-control num" id="capacityInput">
                                            </div>
                                            <div class="invalid-feedback">{{ errors[0] }}</div>
                                        </ValidationProvider>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="d-flex justify-content-md-end align-items-center py-md-8 mb-25">
                                        <span>Pets allowed:</span>
                                        <div class="custom-control custom-radio custom-control-inline">
                                            <input v-model="petsAllowed" type="radio" id="customRadioInline1"
                                                   name="customRadioInline1" class="custom-control-input" :value="true"
                                                   required>
                                            <label class="custom-control-label" for="customRadioInline1">Yes</label>
                                        </div>
                                        <div class="custom-control custom-radio custom-control-inline mr-0">
                                            <input v-model="petsAllowed" type="radio" id="customRadioInline2"
                                                   name="customRadioInline1" class="custom-control-input" :value="false"
                                                   required>
                                            <label class="custom-control-label" for="customRadioInline2">No</label>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-md-end align-items-center py-md-8">
                                        <span>Minors allowed:</span>
                                        <div class="custom-control custom-radio custom-control-inline">
                                            <input v-model="minorsAllowed" type="radio" id="customRadioInline3"
                                                   name="customRadioInline2" class="custom-control-input" :value="true"
                                                   required>
                                            <label class="custom-control-label" for="customRadioInline3">Yes</label>
                                        </div>
                                        <div class="custom-control custom-radio custom-control-inline mr-0">
                                            <input v-model="minorsAllowed" type="radio" id="customRadioInline4"
                                                   name="customRadioInline2" class="custom-control-input" :value="false"
                                                   required>
                                            <label class="custom-control-label" for="customRadioInline4">No</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="text-center">
                                <button class="btn btn-primary px-40" type="submit">Submit</button>
                            </div>
                        </form>
                    </ValidationObserver>

                    <router-link to="/trip/create" class="back-btn text-light-blue position-absolute"><i
                        class="icon-arrow-thin-left"></i></router-link>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
    import httpClient from '@/utils/httpClient';
    import axios from 'axios';
    import AirportTemplate from '../Templates/AirportTemplate';
    import Autocomplete from 'v-autocomplete';
    import DatePicker from 'vue2-datepicker';
    import 'v-autocomplete/dist/v-autocomplete.css';
    import moment from 'moment';

    export default {
        components: {
            Autocomplete,
            DatePicker
        },
        created() {
            if (this.$route.meta.edit) {
                this.getUserLastTrip()
            } else {
                if (this.$route.query.airportFrom) {
                    this.getAirportFrom(this.$route.query.airportFrom)
                }
                if (this.$route.query.airportTo) {
                    this.getAirportTo(this.$route.query.airportTo)
                }
                this.dateRange = this.$route.query.dateRange;
            }
        },
        mounted() {
            this.passengers = this.userIsBusiness ? 0 : 1;
        },
        data() {
            return {
                tripId: null,
                submitting: false,
                airportsFromQuery: [],
                airportsToQuery: [],
                airportFrom: null,
                airportTo: null,
                capacity: 6,
                passengers: null,
                dateRange: {},
                datePickerKey: 0,
                description: null,
                distance: null,
                estimatedPrice: null,
                existingTrip: null,
                petsAllowed: true,
                minorsAllowed: true,
                nearbyFrom: true,
                nearbyTo: true,
                timeOfDay: 'Morning',
                travelTime: null,
                invalidFrom: false,
                invalidTo: false,
                template: AirportTemplate,
                fromInput: {
                    placeholder: 'From',
                    class: 'form-control',
                    id: 'from-input',
                    autocomplete: 'off',
                },
                toInput: {
                    placeholder: 'To',
                    class: 'form-control',
                    id: 'to-input',
                    autocomplete: 'off',
                }
            }
        },
        methods: {
            getUserLastTrip() {
                httpClient.get('api/trips/user').then((res) => {
                    this.tripId = res.data.id;
                    this.airportFrom = res.data.fliesFrom;
                    this.airportTo = res.data.fliesTo;
                    this.dateRange = [
                        moment(res.data.typeFields.rangeFrom).format('MM-DD-YYYY'),
                        moment(res.data.typeFields.rangeTo).format('MM-DD-YYYY')
                    ];
                    this.datePickerKey++
                }).catch(() => {
                    this.$router.push({path: '/trip/create/proposed'})
                })
            },
            beforeToday(date) {
                return date < new Date(new Date().setHours(0, 0, 0, 0));
            },
            clearClassFrom(text) {
                let savedQuery = document.getElementById('from-input').value;
                setTimeout(() => {
                    if (document.getElementById('from-input').value === savedQuery && document.getElementById('from-input').value.length > 0) {
                        this.updateAirportsFrom(document.getElementById('from-input').value);
                    }
                }, 400);

                if (text === '') {
                    this.invalidFrom = false;
                    this.fromInput.class = 'form-control';
                    this.airportsFromQuery = [];
                }
            },
            clearClassTo(text) {
                let savedQuery = document.getElementById('to-input').value;
                setTimeout(() => {
                    if (document.getElementById('to-input').value === savedQuery && document.getElementById('to-input').value.length > 0) {
                        this.updateAirportsTo(document.getElementById('to-input').value);
                    }
                }, 400);

                if (text === '') {
                    this.invalidTo = false;
                    this.toInput.class = 'form-control';
                    this.airportsToQuery = [];
                }
            },
            getLabel(item) {
                if (item) {
                    return item.name;
                } else {
                    this.airportsToQuery = [];
                    return null;
                }
            },
            getNewTripParams() {
                return {
                    flies_from: this.airportFrom.id,
                    flies_to: this.airportTo.id,
                    flight_distance: this.roundNumber(this.distance),
                    capacity: this.capacity,
                    description: this.description,
                    passengers: this.passengers,
                    host_id: this.$store.state.users.user.id,
                    minors_allowed: this.minorsAllowed,
                    pets_allowed: this.petsAllowed,
                    range_from: this.getFormattedDate(this.dateRange[0]),
                    range_to: this.getFormattedDate(this.dateRange[1]),
                    time_of_day: this.timeOfDay,
                    nearby_airports_from: this.nearbyFrom,
                    nearby_airports_to: this.nearbyTo,
                };
            },
            getAirportFrom(airportId) {
                axios.get("/api/airport/" + airportId).then(res => this.airportFrom = res.data)
            },
            getAirportTo(airportId) {
                axios.get("/api/airport/" + airportId).then(res => this.airportTo = res.data)
            },
            getTravelTime() {
                let decimalTime = this.distance / 500 + 0.5;
                let hours = this.getEstimatedHours(decimalTime);
                let label = '';
                if (hours > 1) {
                    label = hours + " hours ";
                } else if (hours == 1) {
                    label = hours + " hour ";
                }

                let minutes = this.getEstimatedMinutes(decimalTime);
                if (minutes > 0 && minutes < 60) {
                    label += minutes + ' min';
                } else if (minutes == 60) {
                    label += '55 min';
                }
                return label;
            },
            getEstimatedHours(decimalTime) {
                return Math.trunc(decimalTime);
            },
            getEstimatedMinutes(decimalTime) {
                return Math.ceil(((decimalTime % 1) * 60) / 5) * 5;
            },
            getFormattedDate(date) {
                return moment(date, 'MM-DD-YYYY').format('YYYY-MM-DD');
            },
            updateAirportsFrom(text) {
                if (text) {
                    httpClient.get("/api/airports/search?filter[search]=" + text)
                        .then((res) => {
                            this.airportsFromQuery = res.data.data.airports;
                            //Don't show already selected airport
                            this.airportsFromQuery = this.airportTo ?
                                this.airportsFromQuery.filter(airport => airport.id !== this.airportTo.id) :
                                this.airportsFromQuery;
                            if (this.airportsFromQuery.length === 0) {
                                this.invalidFrom = true;
                                this.fromInput.class = 'form-control is-invalid';
                            }
                        });
                }
            },
            updateAirportsTo(text) {
                if (text) {
                    httpClient.get("/api/airports/search?filter[search]=" + text)
                        .then((res) => {
                            this.airportsToQuery = res.data.data.airports;
                            this.airportsToQuery = this.airportFrom ?
                                this.airportsToQuery.filter(airport => airport.id !== this.airportFrom.id) :
                                this.airportsToQuery;
                            if (this.airportsToQuery.length === 0) {
                                this.invalidTo = true;
                                this.toInput.class = 'form-control is-invalid';
                            }
                        });
                }
            },
            refreshDistance() {
                if (!this.airportFrom || !this.airportTo || !this.capacity) {
                    this.distance = null;
                    this.estimatedPrice = null;
                    this.travelTime = null;
                } else {
                    this.updateDistance();
                }
            },
            updateDistance() {
                let params = {
                    params: {
                        capacity: this.capacity,
                    }
                };
                httpClient.get("/api/distance/" + this.airportFrom.id + "/" + this.airportTo.id, params).then(res => {
                    this.distance = res.data.miles;
                    this.estimatedPrice = res.data.price;
                    this.travelTime = this.getTravelTime();
                });
            },
            roundNumber(value) {
                return Math.ceil(value);
            },
            createTrip() {
                httpClient.post("/api/proposed-trip", this.getNewTripParams()).then(() => {
                    if (process.env.VUE_APP_ENV == 'production') {
                        window.ga('send', 'event', 'Lead', 'Proposed');
                    }
                    this.$swal({title: 'OK', icon: 'success', text: "The Trip has been Created"})
                        .then(() => this.$router.replace('/trip/search'));
                });
            },
            editTrip() {
                httpClient.patch(`/api/proposed-trip/${this.tripId}`, this.getNewTripParams()).then(() => {
                    this.$swal({title: 'OK', icon: 'success', text: "Your changes have been applied"})
                        .then(() => this.$router.replace('/trip/search'));
                });
            },
            saveTrip() {
                // Validate if trip exists
                if (!this.submitting) {
                    this.submitting = true;

                    if (this.tripId !== null) {
                        this.editTrip()
                    } else {
                        httpClient.get("/api/trip/exists/proposed?flies_from=" + this.airportFrom.id + "&flies_to=" + this.airportTo.id +
                            "&departure_date=" + "&filter[between]=" + this.getFormattedDate(this.dateRange[0]) + "," + this.getFormattedDate(this.dateRange[1]) +
                            "&filter[afterToday]=1").then((res) => {
                            if (res.data) {
                                this.existingTrip = res.data;
                                this.$swal({
                                    title: 'Are you sure?',
                                    icon: 'warning',
                                    text: "A trip with that parameters already exists. What do you want to do?",
                                    showCancelButton: true,
                                    confirmButtonText: 'Join a flight',
                                    cancelButtonText: 'Change Trip Parameters',
                                    allowOutsideClick: false
                                })
                                    .then((result) => {
                                        //Join a flight
                                        if (result.value) {
                                            this.$router.push({
                                                name: 'TripDetail',
                                                params: {trip: this.existingTrip.id}
                                            })
                                        } else {
                                            this.submitting = false;
                                        }
                                    })
                            } else {
                                //No trips with the entered parameters have been created.
                                this.createTrip();
                            }
                        });
                    }
                }
            }
        },
        computed: {
            userIsBusiness() {
                return this.$store.state.users.user.role.name === 'business' || this.$store.state.users.user.role.name === 'broker';
            },
            displayTextArea() {
                const userEmail = this.$store.state.users.user.email;
                return this.$store.state.users.user && userEmail === 'emptyleg@sharedcharter.com' || userEmail === 'emptylegs@sharedcharter.com';
            },
            seatsDesiredRules() {
                return this.userIsBusiness ? 'required|integer|min_value:0' : 'required|integer|min_value:1|empty_leg_restriction'
            }
        },
        watch: {
            capacity() {
                this.refreshDistance();
            }
        }
    }
</script>

<style>

    .seat-input span {
        display: flex;
        flex-flow: wrap;
        align-items: center;
    }

    .invalid-feedback {
        display: block;
    }

    .v-autocomplete-list {
        background-color: white;
        border: solid black 1px;
        z-index: 1;
        color: black;
    }

</style>
